<template>
  <b-container>
    <b-row class="mb-4" align-h="start">
      <b-overlay :show="loading" no-wrap fixed />
      <b-col>
        <h1>Delivery #{{ $route.params.id }}</h1>
      </b-col>
      <b-col class="text-right">
        <b-button variant="primary" @click="$router.push('/deliveries')">
          Back
        </b-button>
      </b-col>
    </b-row>
    <b-row v-if="delivery" class="base-form mb-3">
      <b-col sm="12" md="6" lg="3" class="mb-3">
        <b-form-group label="Status">
          {{ delivery.status | capitalize }}
        </b-form-group>
        <b-form-group label="Fulfillment Time">
          {{ delivery.fulfillment_time | formatDateTime }}
        </b-form-group>
        <b-form-group label="Cutoff Time">
          {{ delivery.cutoff_time | formatDateTime }}
        </b-form-group>
        <b-form-group label="Batch Number" class="mb-0">
          {{ delivery.batch }}
        </b-form-group>
      </b-col>
      <b-col sm="12" md="6" lg="3" class="mb-3">
        <b-form-group label="Delivery Provider">
          {{ delivery.delivery_provider.name }}
        </b-form-group>
        <b-form-group label="Hub">
          {{ delivery.facility.name }}
        </b-form-group>
        <b-form-group label="Network">
          {{ delivery.network.name }}
        </b-form-group>
        <b-form-group label="Delivery Duration" class="mb-0">
          {{ Math.floor(delivery.delivery_duration / 60) }} minutes
          <template v-if="delivery.delivery_duration % 60"
            >{{ delivery.delivery_duration % 60 }} seconds</template
          >
        </b-form-group>
      </b-col>
      <b-col sm="12" md="6" lg="3" class="mb-3">
        <b-form-group label="Address">
          {{ delivery.address | formatAddressStreet }}
        </b-form-group>
        <b-form-group label="Building">
          {{ delivery.address.street }}
        </b-form-group>
        <b-form-group label="Delivery Notes" class="mb-0">
          {{ delivery.delivery_notes ? delivery.delivery_notes : 'None' }}
        </b-form-group>
      </b-col>
      <b-col sm="12" md="6" lg="3" class="mb-3">
        <b-form-group label="Contact Name">
          {{ delivery.contact_name }}
        </b-form-group>
        <b-form-group label="Contact Phone">
          {{ delivery.contact_phone }}
        </b-form-group>
        <b-form-group label="Enroute Notice Sent">
          <template v-if="delivery.enroute_notice_sent">
            {{ delivery.enroute_notice_sent | formatDateTime }}
          </template>
          <template v-else> None </template>
        </b-form-group>
        <b-form-group label="Delivered Notice" class="mb-0">
          <template v-if="delivery.completed_notice_sent">
            {{ delivery.completed_notice_sent | formatDateTime }}
          </template>
          <template v-else> None </template>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row v-if="delivery">
      <b-col v-if="logistics.length" lg="6">
        <b-alert :show="delivery.status == 'pending'" variant="danger">
          <strong>Note:</strong> Logistics costs will only be finalized once
          order has been dispatched.
        </b-alert>
        <b-table
          hover
          no-local-sorting
          small
          :items="logistics"
          :fields="logisticsFields"
        >
          <template #cell(label)="row">
            <div style="padding-left: 13px">{{ row.item.label }}</div>
          </template>
          <template #cell(value)="row">
            <div style="padding-right: 13px">
              <template v-if="row.item.currency">
                {{ (row.item.value / 100) | currency }}
              </template>
              <template v-else>
                {{ row.item.value }}
              </template>
            </div>
          </template>
        </b-table>
      </b-col>
      <b-col lg="6">
        <b-table
          hover
          no-local-sorting
          small
          :items="delivery.network_orders"
          :fields="orderFields"
        >
          <template #cell(number)="row">
            <router-link
              :to="'/orders/' + row.item.id"
              style="padding-left: 13px"
            >
              #{{ row.item.number }}
            </router-link>
          </template>
          <template #cell(subtotal)="row">
            <div style="padding-right: 13px">
              {{ (row.item.subtotal / 100) | currency }}
            </div>
          </template>
        </b-table>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import ResourceAPI from '../../services/ResourceAPI';

export default {
  name: 'Delivery',
  data() {
    return {
      loading: false,
      delivery: null,
      logistics: [],
      logisticsFields: [
        {
          key: 'label',
          label: 'Logistics Costs',
          sortable: false,
          thStyle: { 'padding-left': '20px' },
        },
        {
          key: 'value',
          label: 'Amount',
          sortable: false,
          class: 'text-right',
          thStyle: { 'padding-right': '20px' },
        },
      ],
      orderFields: [
        {
          key: 'number',
          label: 'Order ID',
          sortable: false,
          thStyle: { 'padding-left': '20px' },
        },
        {
          key: 'name',
          label: 'Customer',
          sortable: false,
        },
        {
          key: 'subtotal',
          label: 'Amount',
          sortable: false,
          class: 'text-right',
          thStyle: { 'padding-right': '20px' },
        },
      ],
    };
  },

  mounted() {
    this.loadDelivery();
  },
  methods: {
    loadDelivery() {
      this.loading = true;
      return ResourceAPI.getRecords(`deliveries/${this.$route.params.id}`, {
        with: 'address,deliveryProvider,networkOrders,network,facility',
        appends: 'subtotal,orderCount,sponsoredBy,distributedTo',
      })
        .then((data) => {
          this.delivery = data.data;
          this.loadLogisitcs();
          this.loading = false;
        })
        .catch((error) => alert(error));
    },
    loadLogisitcs() {
      let sponsoredBy = this.capitalize(this.delivery.sponsored_by_type);
      if (this.delivery.sponsoredBy) {
        sponsoredBy += ` - ${this.delivery.sponsoredBy.name}`;
      }

      let distributedTo = this.capitalize(this.delivery.sponsored_by_type);
      if (this.delivery.distributedTo) {
        distributedTo += ` - ${this.delivery.distributedTo.name}`;
      }

      this.logistics = [
        {
          label: 'Total Orders',
          value: this.delivery.orderCount,
          currency: false,
        },
        {
          label: 'Gross Revenue',
          value: this.delivery.subtotal,
          currency: true,
        },
        {
          label: 'Customer Fees',
          value: this.delivery.customer_fees,
          currency: true,
        },
        {
          label: 'Tip',
          value: this.delivery.tip_total,
          currency: true,
        },
        {
          label: 'Logistics Costs',
          value: this.delivery.logistics_cost,
          currency: true,
        },
        {
          label: 'Sponsored By',
          value: sponsoredBy,
          currency: false,
        },
        {
          label: 'Sponsored Amount',
          value: this.delivery.sponsored_amount,
          currency: true,
        },
        {
          label: 'Distributed To',
          value: distributedTo,
          currency: false,
        },
        {
          label: 'Distributed Amount',
          value: this.delivery.distributed_amount,
          currency: true,
        },
      ];
    },
  },
};
</script>
